import {
  BOARD_W,
  BOARD_H,
  MAX_QUEUE,
  LOCK_DELAY,
  MAX_LOCK_RESETS,
  DAS,
  ARR,
  SOFT_DROP_INTERVAL,
  CLEAR_FLASH,
  gravityInterval,
} from './constants';
import { PIECES, SHAPES, COLORS, SPAWN, kicksFor } from './tetrominoes';

export const STATE = {
  READY: 'ready',
  PLAYING: 'playing',
  PAUSED: 'paused',
  GAMEOVER: 'gameover',
};

const CLEAR_POINTS = [0, 100, 300, 500, 800];
const MAX_PARTICLES = 900;

// Pure-ish game simulation. It owns the board, the active piece, timing and
// effects; the renderer only reads from it and never mutates it.
export class Game {
  constructor() {
    this.version = 0;
    this.reset();
  }

  // --- lifecycle -----------------------------------------------------------

  reset() {
    this.board = new Uint8Array(BOARD_W * BOARD_H);
    this.bag = [];
    this.queue = [];
    for (let i = 0; i < MAX_QUEUE; i += 1) this.queue.push(this.nextType());

    this.active = null;
    this.holdType = null;
    this.holdUsed = false;

    this.score = 0;
    this.lines = 0;
    this.level = 1;
    this.combo = 0;
    this.backToBack = false;

    this.gravityAcc = 0;
    this.lockTimer = 0;
    this.lockResets = 0;
    this.dasTimer = 0;
    this.arrTimer = 0;
    this.lastDir = 0;
    this.held = { left: false, right: false };
    this.softDropping = false;

    this.clearTimer = 0;
    this.clearRows = [];
    this.particles = [];
    this.flashes = [];
    this.shake = 0;
    this.time = 0;

    this.state = STATE.READY;
    this.version += 1;
  }

  start() {
    this.reset();
    this.state = STATE.PLAYING;
    this.spawn();
    this.version += 1;
  }

  togglePause() {
    if (this.state === STATE.PLAYING) this.state = STATE.PAUSED;
    else if (this.state === STATE.PAUSED) this.state = STATE.PLAYING;
    else return;
    this.version += 1;
  }

  // --- randomness ----------------------------------------------------------

  refillBag() {
    const bag = [...PIECES];
    for (let i = bag.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [bag[i], bag[j]] = [bag[j], bag[i]];
    }
    this.bag.push(...bag);
  }

  nextType() {
    if (this.bag.length === 0) this.refillBag();
    return this.bag.shift();
  }

  // --- board queries -------------------------------------------------------

  cells(type, rot) {
    const states = SHAPES[type];
    return states[rot % states.length];
  }

  collides(type, rot, x, y) {
    const cells = this.cells(type, rot);
    for (let i = 0; i < cells.length; i += 1) {
      const cx = x + cells[i].x;
      const cy = y + cells[i].y;
      if (cx < 0 || cx >= BOARD_W || cy >= BOARD_H) return true;
      if (cy >= 0 && this.board[cy * BOARD_W + cx]) return true;
    }
    return false;
  }

  grounded() {
    if (!this.active) return false;
    const { type, rot, x, y } = this.active;
    return this.collides(type, rot, x, y + 1);
  }

  // --- spawning / locking --------------------------------------------------

  spawn() {
    const type = this.queue.shift();
    this.queue.push(this.nextType());
    const { x, y } = SPAWN[type];
    this.active = { type, rot: 0, x, y };

    this.gravityAcc = 0;
    this.lockTimer = 0;
    this.lockResets = 0;
    this.holdUsed = false;
    this.version += 1;

    if (this.collides(type, 0, x, y)) {
      this.state = STATE.GAMEOVER;
      this.active = null;
      this.version += 1;
    }
  }

  lockPiece() {
    const { type, rot, x, y } = this.active;
    const id = PIECES.indexOf(type) + 1;
    const cells = this.cells(type, rot);
    for (let i = 0; i < cells.length; i += 1) {
      const cx = x + cells[i].x;
      const cy = y + cells[i].y;
      if (cy >= 0 && cy < BOARD_H && cx >= 0 && cx < BOARD_W) {
        this.board[cy * BOARD_W + cx] = id;
      }
    }

    const rows = [];
    for (let ry = 0; ry < BOARD_H; ry += 1) {
      let full = true;
      for (let cx = 0; cx < BOARD_W; cx += 1) {
        if (!this.board[ry * BOARD_W + cx]) {
          full = false;
          break;
        }
      }
      if (full) rows.push(ry);
    }

    this.active = null;
    if (rows.length) this.applyClear(rows);
    else {
      this.combo = 0;
      this.spawn();
    }
    this.version += 1;
  }

  applyClear(rows) {
    const n = rows.length;
    let pts = CLEAR_POINTS[n] * this.level;
    if (this.combo > 0) pts += 50 * this.combo * this.level;
    if (this.backToBack && n === 4) pts = Math.round(pts * 1.5);

    this.score += pts;
    this.lines += n;
    this.level = Math.floor(this.lines / 10) + 1;
    this.combo += 1;
    this.backToBack = n === 4;

    this.clearRows = rows;
    this.clearTimer = CLEAR_FLASH;

    rows.forEach((row) => this.spawnRowBurst(row));
  }

  finishClear() {
    const rows = this.clearRows;
    const next = new Uint8Array(BOARD_W * BOARD_H);
    let dest = BOARD_H - 1;
    for (let y = BOARD_H - 1; y >= 0; y -= 1) {
      if (rows.indexOf(y) !== -1) continue;
      for (let x = 0; x < BOARD_W; x += 1) {
        next[dest * BOARD_W + x] = this.board[y * BOARD_W + x];
      }
      dest -= 1;
    }
    this.board = next;
    this.clearRows = [];
    this.clearTimer = 0;
    this.spawn();
  }

  // --- actions -------------------------------------------------------------

  move(dir) {
    if (!this.active) return false;
    const { type, rot, x, y } = this.active;
    if (!this.collides(type, rot, x + dir, y)) {
      this.active.x += dir;
      if (this.grounded()) this.resetLock();
      return true;
    }
    return false;
  }

  rotate(dir) {
    if (!this.active) return false;
    const { type, rot, x, y } = this.active;
    if (SHAPES[type].length === 1) return false;
    const to = (rot + (dir > 0 ? 1 : 3)) % 4;
    const kicks = kicksFor(type, rot, to) || [[0, 0]];
    for (let i = 0; i < kicks.length; i += 1) {
      const nx = x + kicks[i][0];
      const ny = y - kicks[i][1]; // table is y-up, board is y-down
      if (!this.collides(type, to, nx, ny)) {
        this.active.rot = to;
        this.active.x = nx;
        this.active.y = ny;
        if (this.grounded()) this.resetLock();
        return true;
      }
    }
    return false;
  }

  softStep() {
    if (!this.active) return;
    const { type, rot, x, y } = this.active;
    if (!this.collides(type, rot, x, y + 1)) {
      this.active.y += 1;
      this.score += 1;
      this.lockTimer = 0;
      this.version += 1;
    }
  }

  holdPiece() {
    if (!this.active || this.holdUsed) return;
    const current = this.active.type;
    if (this.holdType) {
      const swap = this.holdType;
      this.holdType = current;
      const { x, y } = SPAWN[swap];
      this.active = { type: swap, rot: 0, x, y };
    } else {
      this.holdType = current;
      this.spawn();
    }
    this.holdUsed = true;
    this.gravityAcc = 0;
    this.lockTimer = 0;
    this.lockResets = 0;
    this.version += 1;
  }

  hardDrop() {
    if (!this.active) return;
    const { type, rot, x, y } = this.active;
    let drop = 0;
    while (!this.collides(type, rot, x, y + drop + 1)) drop += 1;
    this.active.y += drop;
    this.score += 2 * drop;
    this.shake = Math.min(13, 3 + drop * 0.6);
    this.spawnDropDust();
    this.lockPiece();
  }

  resetLock() {
    if (this.lockResets < MAX_LOCK_RESETS) {
      this.lockTimer = 0;
      this.lockResets += 1;
    }
  }

  // --- input ---------------------------------------------------------------

  input(action, pressed) {
    if (action === 'pause') {
      if (pressed) this.togglePause();
      return;
    }
    if (action === 'restart') {
      if (pressed) this.start();
      return;
    }
    const canPlay = this.state === STATE.PLAYING && this.active && this.clearTimer <= 0;
    if (action === 'start') {
      if (pressed && (this.state === STATE.READY || this.state === STATE.GAMEOVER)) this.start();
      return;
    }
    if (!canPlay) return;

    if (action === 'left' || action === 'right') {
      const dir = action === 'right' ? 1 : -1;
      this.held[action] = pressed;
      if (pressed) {
        this.move(dir);
        this.lastDir = dir;
      } else if (!this.held.left && !this.held.right) {
        this.lastDir = 0;
      } else {
        this.lastDir = this.held.right ? 1 : -1;
      }
      this.dasTimer = 0;
      this.arrTimer = 0;
      return;
    }
    if (action === 'soft') {
      this.softDropping = pressed;
      return;
    }
    if (action === 'down') {
      if (pressed) this.softStep();
      return;
    }
    if (action === 'cw') {
      if (pressed) this.rotate(1);
      return;
    }
    if (action === 'ccw') {
      if (pressed) this.rotate(-1);
      return;
    }
    if (action === 'hard') {
      if (pressed) this.hardDrop();
      return;
    }
    if (action === 'hold') {
      if (pressed) this.holdPiece();
    }
  }

  // --- effects -------------------------------------------------------------

  spawnRowBurst(row) {
    const wy = BOARD_H - (row + 0.5);
    for (let x = 0; x < BOARD_W; x += 1) {
      const id = this.board[row * BOARD_W + x] || 1;
      const color = COLORS[PIECES[id - 1]] || [1, 0.4, 0.8];
      for (let k = 0; k < 3; k += 1) {
        this.pushParticle({
          x: x + 0.5,
          y: wy,
          vx: (Math.random() - 0.5) * 7,
          vy: Math.random() * 6 + 1.5,
          life: 420 + Math.random() * 380,
          max: 800,
          size: 0.12 + Math.random() * 0.16,
          color,
        });
      }
    }
    this.flashes.push({ row, t: CLEAR_FLASH, max: CLEAR_FLASH });
  }

  spawnDropDust() {
    if (!this.active) return;
    const { type, rot, x, y } = this.active;
    const cells = this.cells(type, rot);
    let bottom = -Infinity;
    for (let i = 0; i < cells.length; i += 1) bottom = Math.max(bottom, y + cells[i].y);
    const color = COLORS[type] || [1, 0.4, 0.8];
    for (let i = 0; i < cells.length; i += 1) {
      const cx = x + cells[i].x;
      const cy = y + cells[i].y;
      if (cy < 0) continue;
      this.pushParticle({
        x: cx + 0.5,
        y: BOARD_H - bottom - 0.5,
        vx: (Math.random() - 0.5) * 5,
        vy: Math.random() * 3,
        life: 220 + Math.random() * 200,
        max: 420,
        size: 0.1 + Math.random() * 0.1,
        color,
      });
    }
  }

  pushParticle(p) {
    if (this.particles.length >= MAX_PARTICLES) this.particles.shift();
    this.particles.push(p);
  }

  updateEffects(dt) {
    const s = dt / 1000;
    const parts = this.particles;
    for (let i = parts.length - 1; i >= 0; i -= 1) {
      const p = parts[i];
      p.life -= dt;
      if (p.life <= 0) {
        parts.splice(i, 1);
        continue;
      }
      p.x += p.vx * s;
      p.y += p.vy * s;
      p.vy -= 22 * s;
      p.vx *= 1 - 1.6 * s;
    }
    for (let i = this.flashes.length - 1; i >= 0; i -= 1) {
      this.flashes[i].t -= dt;
      if (this.flashes[i].t <= 0) this.flashes.splice(i, 1);
    }
    if (this.shake > 0) this.shake = Math.max(0, this.shake - dt * 0.02);
  }

  // --- main update ---------------------------------------------------------

  update(dt) {
    dt = Math.min(dt, 80);
    this.time += dt;
    this.updateEffects(dt);

    if (this.state !== STATE.PLAYING) return;

    if (this.clearTimer > 0) {
      this.clearTimer -= dt;
      if (this.clearTimer <= 0) this.finishClear();
      return;
    }
    if (!this.active) return;

    // Horizontal auto-repeat.
    if (this.lastDir !== 0) {
      this.dasTimer += dt;
      if (this.dasTimer >= DAS) {
        this.arrTimer += dt;
        while (this.arrTimer >= ARR) {
          this.arrTimer -= ARR;
          if (!this.move(this.lastDir)) break;
        }
      }
    }

    if (this.grounded()) {
      this.lockTimer += dt;
      if (this.lockTimer >= LOCK_DELAY || this.lockResets > MAX_LOCK_RESETS) {
        this.lockPiece();
      }
      return;
    }

    const interval = this.softDropping ? SOFT_DROP_INTERVAL : gravityInterval(this.level);
    this.gravityAcc += dt;
    let dropped = false;
    while (this.gravityAcc >= interval) {
      this.gravityAcc -= interval;
      const { type, rot, x, y } = this.active;
      if (this.collides(type, rot, x, y + 1)) break;
      this.active.y += 1;
      if (this.softDropping) {
        this.score += 1;
        dropped = true;
      }
      this.lockTimer = 0;
    }
    if (dropped) this.version += 1;
  }

  // --- render / hud snapshots ---------------------------------------------

  getGhostY() {
    if (!this.active) return 0;
    const { type, rot, x, y } = this.active;
    let gy = y;
    while (!this.collides(type, rot, x, gy + 1)) gy += 1;
    return gy;
  }

  getHud() {
    return {
      score: this.score,
      lines: this.lines,
      level: this.level,
      combo: this.combo,
      state: this.state,
      next: this.queue.slice(0, 3),
      hold: this.holdType,
    };
  }
}
